import { useZionFormContext } from '@fs/zion-form'
import { BillboardButton, Card, H2, Separator, useOverlay } from '@fs/zion-ui'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import cloneDeep from 'lodash.clonedeep'
import { ContentAdd } from '@fs/zion-icon'
import { getNextUuid, useSlsStateContext } from '@fs/zion-sls'
import PersonFormElement from '../shared/PersonFormElement'
import EventFieldsSelector from './EventFieldsSelector'
import { createFieldValueFromChip, EVENT_FIELD_TYPES as eventFieldTypes, removeEventField } from './utils/eventUtils'
import useTreatedFieldChips from '../../../../hooks/useTreatedFieldChips'
import { translateEventFieldChips } from '../../../../utils/translationUtils'
import { shouldDateFieldBeReadOnly } from '../../../../../shared/utils/dateUtils'

const EventFieldsCard = ({ heading, fieldType, fieldSelectorHeading }) => {
  const { types } = useSlsStateContext()
  const [t] = useTranslation()
  const eventFieldsSelectorOverlay = useOverlay()
  const { treatedFieldChipData } = useTreatedFieldChips(types, [], [], [], translateEventFieldChips)
  const fieldTypeKey = useMemo(
    () => Object.keys(eventFieldTypes).find((key) => eventFieldTypes[key] === fieldType),
    [fieldType]
  )
  const fieldTypeName = useMemo(() => fieldTypeKey.toLowerCase(), [fieldTypeKey])
  const mainChip = treatedFieldChipData.find((c) => c.value === fieldTypeKey)

  const formMethods = useZionFormContext()
  const { getValues, watch, setValue, trigger } = formMethods

  const fields = watch(fieldType)
  const isDateReadOnly = shouldDateFieldBeReadOnly(fields)

  const handleMainFieldAdd = useCallback(() => {
    if (fieldTypeKey === eventFieldTypes.EVENT) return

    const formValues = getValues()
    const currentValues = cloneDeep(formValues[fieldType])
    const newFieldUuid = getNextUuid()
    const newAddedValue = createFieldValueFromChip({
      uuid: newFieldUuid,
      chip: mainChip,
      formValues,
      superType: fieldTypeKey,
    })

    setValue(fieldType, [...currentValues, newAddedValue], { shouldDirty: true })
    trigger(newFieldUuid)
  }, [getValues, setValue, mainChip, fieldType, fieldTypeKey, trigger])

  const handleFieldRemove = useCallback(
    (field) => {
      const formValues = getValues()
      const currentRemovedFields = formValues?.removedFields || []
      const updatedFields = cloneDeep(formValues[fieldType].filter((f) => f.id !== field.id))
      removeEventField({ field, form: formMethods, fieldTypeKey, removedFieldsArray: currentRemovedFields })

      setValue(fieldType, updatedFields, { shouldDirty: true })
      trigger() // Revalidate form after removing fields
    },
    [getValues, setValue, trigger, fieldType, formMethods, fieldTypeKey]
  )

  const isMainfieldSelected = useMemo(() => {
    return fields?.some((field) => field.fieldType === fieldTypeKey)
  }, [fields, fieldTypeKey])

  return (
    <>
      <Card edges="border">
        {heading && <H2 size="H6">{heading}</H2>}
        {fields
          ?.filter((field) => field?.typeData?.editable !== false)
          ?.map((field) => (
            <PersonFormElement
              key={field.id}
              field={field}
              onDelete={() => handleFieldRemove(field)}
              isDateReadOnly={isDateReadOnly}
            />
          ))}
        {fieldType !== eventFieldTypes.EVENT && !isMainfieldSelected && !isDateReadOnly && (
          <BillboardButton
            Icon={ContentAdd}
            emphasis="low"
            onClick={handleMainFieldAdd}
            data-testid={`edit-event-add-${fieldTypeName}-field`}
            linkName={`Edit event add ${fieldTypeName} field`}
          >
            {mainChip?.label}
          </BillboardButton>
        )}
        {!isDateReadOnly && (
          <EventFieldsSelector
            title={fieldSelectorHeading || t('edit-index.add-more-info.button', 'Add More Information')}
            overlay={eventFieldsSelectorOverlay}
            fieldTypeKey={fieldTypeKey}
          />
        )}
      </Card>
      <Separator size="xs" />
    </>
  )
}

export default EventFieldsCard
